import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import Breadcrumbs from '../../components/navigation/Breadcrumbs';
import { Link } from "react-router-dom";
import ExternalShareIcon from "../../components/icons/ExternalShareIcon";

type TabType = 'steps' | 'terms' | 'documents';

const MosqueSearchService: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('steps');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'steps':
        return (
          <div className={`space-y-6 text-gray-700 leading-relaxed text-lg ${isRTL ? 'text-right' : 'text-left'}`}>
            <ul className={`list-disc space-y-2 ${isRTL ? 'list-inside mr-4' : 'list-outside ml-6'}`}>
              <li>{t('mosqueSearchStep1')}</li>
              <li>{t('mosqueSearchStep2')}</li>
              <li>{t('mosqueSearchStep3')}</li>
            </ul>
          </div>
        );
      
      case 'terms':
        return (
          <div className={`space-y-6 text-gray-700 leading-relaxed text-lg ${isRTL ? 'text-right' : 'text-left'}`}>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mosqueSearchTermsTitle')}</h3>
              <ul className={`list-disc space-y-2 ${isRTL ? 'list-inside mr-4' : 'list-outside ml-6'}`}>
                <li>{t('mosqueSearchTerm2')}</li>
                <li>{t('mosqueSearchTerm3')}</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mosqueSearchObligations')}</h3>
              <ul className={`list-disc space-y-2 ${isRTL ? 'list-inside mr-4' : 'list-outside ml-6'}`}>
                <li>{t('mosqueSearchObligation2')}</li>
                <li>{t('mosqueSearchObligation3')}</li>
              </ul>
            </div>
          </div>
        );
      
      case 'documents':
        return (
          <div className={`space-y-6 text-gray-700 leading-relaxed text-lg ${isRTL ? 'text-right' : 'text-left'}`}>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mosqueSearchRequiredDocuments')}</h3>
              <ul className={`list-disc space-y-2 ${isRTL ? 'list-inside mr-4' : 'list-outside ml-6'}`}>
                <li>{t('mosqueSearchDocument1')}</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('mosqueSearchImportantNotes')}</h3>
              <ul className={`list-disc space-y-2 ${isRTL ? 'list-inside mr-4' : 'list-outside ml-6'}`}>
                <li>{t('mosqueSearchNote1')}</li>
                <li>{t('mosqueSearchNote2')}</li>
                <li>{t('mosqueSearchNote3')}</li>
              </ul>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        <Breadcrumbs className="mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {/* Title */}
            <div className={`mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('mosqueSearchServiceTitle')}
              </h1>
              <span className="inline-block bg-gray-100 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg">
                {t('mosqueSearchCategory')}
              </span>
            </div>

            {/* Description */}
            <div className={`mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
              <p className="text-gray-700 leading-relaxed text-lg">
                {t('mosqueSearchServiceDesc')}
              </p>
            </div>

            {/* Service Level Agreement Link */}
            <div className={`mb-8 ${isRTL ? 'text-right' : 'text-left'}`}>
              <button className="inline-flex items-center gap-2 text-sa-600 hover:text-sa-700 transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="font-semibold">{t('mosqueSearchServiceAgreement')}</span>
              </button>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className={`flex gap-8 ${isRTL ? '' : 'flex-row-reverse'}`}>
                <button
                  onClick={() => setActiveTab('steps')}
                  className={`pb-4 px-2 font-semibold transition-colors flex items-center gap-2 ${
                    activeTab === 'steps'
                      ? 'text-sa-600 border-b-2 border-sa-600'
                      : 'text-gray-600 hover:text-sa-600'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('mosqueSearchSteps')}
                </button>
                <button
                  onClick={() => setActiveTab('terms')}
                  className={`pb-4 px-2 font-semibold transition-colors flex items-center gap-2 ${
                    activeTab === 'terms'
                      ? 'text-sa-600 border-b-2 border-sa-600'
                      : 'text-gray-600 hover:text-sa-600'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('mosqueSearchTerms')}
                </button>
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`pb-4 px-2 font-semibold transition-colors flex items-center gap-2 ${
                    activeTab === 'documents'
                      ? 'text-sa-600 border-b-2 border-sa-600'
                      : 'text-gray-600 hover:text-sa-600'
                  }`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t('mosqueSearchDocuments')}
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
              {renderTabContent()}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sticky top-8">
              {/* Start Service Button */}
              <Link
                to="/mosquesearch"
                className="w-full bg-sa-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-sa-700 transition-colors inline-block text-center mb-6"
              >
                {t('mosqueSearchStartService')}
              </Link>
              {/* Service Details */}
              <div className={`space-y-6 mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchBeneficiaries')}</span>
                  </div>
                  <p className={`text-gray-600 text-sm ${isRTL ? 'mr-7' : 'ml-7'}`}>{t('mosqueSearchBeneficiariesDesc')}</p>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchServiceDuration')}</span>
                  </div>
                  <p className={`text-gray-600 text-sm ${isRTL ? 'mr-7' : 'ml-7'}`}>{t('mosqueSearchServiceDurationDesc')}</p>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 21h8M12 17v4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchServiceChannels')}</span>
                  </div>
                  <p className={`text-gray-600 text-sm ${isRTL ? 'mr-7' : 'ml-7'}`}>{t('mosqueSearchDigital')}</p>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchServiceFees')}</span>
                  </div>
                  <p className={`text-gray-600 text-sm ${isRTL ? 'mr-7' : 'ml-7'}`}>{t('mosqueSearchFree')}</p>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchServiceLanguage')}</span>
                  </div>
                  <p className={`text-gray-600 text-sm ${isRTL ? 'mr-7' : 'ml-7'}`}>{t('mosqueSearchArabic')}</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className={`border-t border-gray-200 pt-6 space-y-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchFAQ')}</span>
                  </div>
                  <a
                    href="https://www.moia.gov.sa/Pages/faq.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sa-600 hover:text-sa-700 text-sm mt-1 block"
                  >
                    <span className={`flex max-w-full items-center gap-2 ${isRTL ? "" : "flex-row-reverse"}`}>
                      <span className="min-w-0 truncate">{t('mosqueSearchFAQ')}</span>
                      <span className="shrink-0 text-sa-600" aria-hidden="true">
                        <ExternalShareIcon size={18} className="shrink-0" />
                      </span>
                    </span>
                  </a>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchPhone')}</span>
                  </div>
                  <a href="tel:1933" className="text-gray-600 hover:text-sa-600 text-sm mt-1 block">
                    <span className={`flex max-w-full items-center gap-2 ${isRTL ? "" : "flex-row-reverse"}`}>
                      <span>1933</span>
                      <span className="shrink-0 text-sa-600" aria-hidden="true">
                        <ExternalShareIcon size={18} className="shrink-0" />
                      </span>
                    </span>
                  </a>
                </div>

                <div>
                  <div className={`flex items-center gap-2 mb-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-sa-600">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 6l-10 7L2 6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">{t('mosqueSearchEmail')}</span>
                  </div>
                  <a
                    href="mailto:info@moia.gov.sa"
                    className="text-gray-600 hover:text-sa-600 text-sm mt-1 block"
                  >
                    <span className={`flex max-w-full items-center gap-2 ${isRTL ? '' : 'flex-row-reverse'}`}>
                      <span className="min-w-0 truncate" dir="ltr">info@moia.gov.sa</span>
                      <span className="shrink-0 text-sa-600" aria-hidden="true">
                        <ExternalShareIcon size={18} className="shrink-0" />
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MosqueSearchService;

